/* eslint-disable */
import { apiURL } from 'config/baseURL';
import { FindSKUByQueryResponse } from '@nimbly-technologies/nimbly-common';
import { getToken } from 'reducers/api';
import * as actions from 'reducers/skuManagement/skus.action';
import * as types from 'reducers/skuManagement/skus.actionTypes';
import { SkusState } from 'reducers/skuManagement/skus.reducer';
import { call, put, select } from 'typed-redux-saga';
import { takeLatest } from 'redux-saga/effects';
import { RootState } from 'store/rootReducers';
import { ALL_SKU_LIST } from 'reducers/skuManagement/type.d';
import { toast } from 'helpers/toast';
import i18n from 'i18n';

function* fetchSkus() {
	try {
		const authToken = yield* call(getToken);
		const skuState: SkusState = yield* select((state: RootState) => state.sku);

		const options = {
			method: 'GET',
			headers: {
				Authorization: authToken,
			},
		};

		const convertFiltersToArray = (type: string, filters: any[]) =>
			filters.reduce((prev: Array<string[]>, filter) => [...prev, [type, filter.value]], []);

		const filterQuery = [
			...convertFiltersToArray('categories', skuState.filters.categories),
			...convertFiltersToArray('siteIDs', skuState.filters.sites),
			...convertFiltersToArray('uoms', skuState.filters.uoms),
		];

		const query: Record<string, string> = {
			page: String(skuState.pageIndex),
			limit: String(skuState.limit),
			sortFields: skuState.sortBy,
			sortDirections: skuState.sortDirection,
			search: skuState.searchQuery,
			withDisabled: 'false',
		};

		const queryStr = new URLSearchParams([...Object.entries(query), ...filterQuery]).toString();
		const fetchSkusURL = `${apiURL}/skus/search?${queryStr}`;

		const response = yield* call(fetch, fetchSkusURL, options);

		if (response?.status === 200) {
			const responseJson = yield response.json();

			const responseData: FindSKUByQueryResponse = responseJson.data;

			yield put(
				actions.fetchSkusAync.success({
					skus: responseData.docs,
					totalSkus: responseData.totalDocs || 0,
					lastUpdatedAt: responseData.lastUpdatedAt,
				}),
			);
			return responseData;
		} else {
			const responseData = yield response.json();
			yield put(actions.fetchSkusAync.failure({ error: responseData.message }));
			return null;
		}
	} catch (e) {
		yield put(actions.fetchSkusAync.failure({ error: 'Failed to Fetch Skus' }));
		return null;
	}
}

function* fetchAllSkus() {
	try {
		const authToken = yield* call(getToken);
		const skuState: SkusState = yield* select((state: RootState) => state.sku);

		const options = {
			method: 'GET',
			headers: {
				Authorization: authToken,
			},
		};

		const fetchSkusURL = `${apiURL}/skus/?props=skuID&props=name&withDisabled=false`;

		const response = yield* call(fetch, fetchSkusURL, options);

		if (response?.status === 200) {
			const responseJson = yield response.json();

			const responseData: ALL_SKU_LIST[] = responseJson.data;

			yield put(
				actions.fetchAllSkusAsync.success({
					skusAll: responseData,
				}),
			);
			return responseData;
		} else {
			const responseData = yield response.json();
			yield put(actions.fetchAllSkusAsync.failure({ error: responseData.message }));
			return null;
		}
	} catch (e) {
		yield put(actions.fetchAllSkusAsync.failure({ error: 'Failed to Fetch Skus' }));
		return null;
	}
}

function* bulkDeleteSkus(action: ReturnType<typeof actions.bulkDeleteSkusAsync.request>) {
	try {
		const authToken = yield* call(getToken);
		const { skuIds } = action.payload;

		const options = {
			method: 'DELETE',
			headers: {
				Authorization: authToken,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ skuIds }),
		};

		const bulkDeleteURL = `${apiURL}/skus/bulk-delete`;
		const response = yield* call(fetch, bulkDeleteURL, options);

		if (response?.status === 200) {
			const responseJson = yield response.json();
			const { data } = responseJson;

			if (data && data.success) {
				// Extract successfully deleted SKU IDs from the results
				const successfullyDeletedIds = data.results
					.filter((result: any) => result.success)
					.map((result: any) => result.id);

				yield put(
					actions.bulkDeleteSkusAsync.success({
						deletedSkuIds: successfullyDeletedIds,
					}),
				);

				// Refetch SKUs to update the list
				yield put(actions.fetchSkusAync.request());

				// If some SKUs failed to delete, show a notification
				if (data.deletedCount < skuIds.length) {
					const failedCount = skuIds.length - data.deletedCount;
					toast.warning(
						i18n.t('message.skuManagementPage.partialDeleteSuccess', {
							successCount: data.deletedCount,
							failedCount,
						}),
					);
				}

				return data;
			} else {
				// All SKUs failed to delete
				yield put(
					actions.bulkDeleteSkusAsync.failure({
						error: 'No SKUs were deleted. Please check your selection and try again.',
					}),
				);
				return null;
			}
		} else if (response?.status === 400) {
			const responseData = yield* call([response, 'json']);
			yield put(
				actions.bulkDeleteSkusAsync.failure({
					error: responseData.error || 'Invalid request. Please check your selection.',
				}),
			);
			return null;
		} else if (response?.status === 404) {
			const responseData = yield* call([response, 'json']);
			yield put(
				actions.bulkDeleteSkusAsync.failure({
					error: responseData.error || 'No valid SKUs found for deletion.',
				}),
			);
			return null;
		} else {
			const responseData = yield* call([response, 'json']);
			yield put(
				actions.bulkDeleteSkusAsync.failure({
					error: responseData.error || 'Failed to delete SKUs. Please try again.',
				}),
			);
			return null;
		}
	} catch (e) {
		yield put(
			actions.bulkDeleteSkusAsync.failure({
				error: 'Network error occurred. Please check your connection and try again.',
			}),
		);
		return null;
	}
}

export default function* skusSaga() {
	yield takeLatest(types.FETCH_SKUS_REQUEST, fetchSkus);
	yield takeLatest(types.FETCH_ALL_SKUS_REQUEST, fetchAllSkus);
	yield takeLatest(types.SET_SKU_PAGE_INDEX, fetchSkus);
	yield takeLatest(types.SET_SKU_FILTER_QUERY, fetchSkus);
	yield takeLatest(types.SET_SKU_SEARCH_QUERY, fetchSkus);
	yield takeLatest(types.SET_SKU_SORT, fetchSkus);
	yield takeLatest(types.BULK_DELETE_SKUS_REQUEST, bulkDeleteSkus);
}
